import {
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Link, useParams } from "react-router-dom";

import DeleteIcon from "../../components/icons/DeleteIcon";
import EditIcon from "../../components/icons/EditIcon";
import PromptModal from "../../components/modals/PromptModal";

import { DAYS_OF_THE_WEEK } from "../../config/constants";
import { colors } from "../../config/theme";
import { useCookie } from "../../hooks/useCookie";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import Services from "./Services";
import {
  Chip,
  cleanUrl,
  Dash,
  getOfferUpdateLink,
  IconAction,
  statusTone,
} from "../../helpers/general";
import { tdStyle, thStyle, WEEK_EN } from "../../helpers/constants";
import {
  ACCENT,
  Card,
  cardStyle,
  EmptyState,
  InfoRow,
  PillTabs,
  Stars,
  StatTile,
  TabKey,
} from "./PlacesTable";
import { LocationIcon } from "../../components/icons";

/* ─────────────── Tabs content ─────────────── */

function AboutTab({ data }: { data?: Place }) {
  const today = WEEK_EN[new Date().getDay()];

  return (
    <div className="row g-3">
      <div className="col-lg-6 d-flex flex-column gap-3">
        <Card title="Coordonnées">
          <InfoRow
            icon="phone"
            href={data?.phone ? `tel:${data.phone}` : undefined}
          >
            {data?.phone}
          </InfoRow>
          <InfoRow
            icon="envelop"
            href={data?.email ? `mailto:${data.email}` : undefined}
          >
            {data?.email}
          </InfoRow>
          <InfoRow icon="web" href={data?.website}>
            {data?.website && cleanUrl(data.website)}
          </InfoRow>
          <InfoRow icon="instagram" href={data?.instagramURL}>
            {data?.instagramURL &&
              data.instagramURL.replace(
                /^https?:\/\/(www\.)?instagram\.com\/(.+?)\/?$/,
                "$2",
              )}
          </InfoRow>
          <InfoRow icon="facebook_2" href={data?.facebookURL}>
            {data?.facebookURL &&
              (data.facebookURL.includes("profile.php?id")
                ? data.name
                : data.facebookURL.replace(
                    /^https:\/\/www\.facebook\.com\//,
                    "",
                  ))}
          </InfoRow>
          {!data?.phone &&
            !data?.email &&
            !data?.website &&
            !data?.instagramURL &&
            !data?.facebookURL && <Dash />}
        </Card>

        <Card title="Détails">
          <InfoRow icon="cutlery">{data?.keywords}</InfoRow>
          <InfoRow icon="wallet">
            {data?.paymentMethods?.length
              ? `Paiement par ${data.paymentMethods.join(", ")}`
              : undefined}
          </InfoRow>
          <InfoRow icon="parking">
            Parking : {data?.hasParking ? "Disponible" : "Non"}
          </InfoRow>
        </Card>
      </div>

      <div className="col-lg-6">
        <Card title="Horaires d'ouverture">
          {data?.openingHours ? (
            DAYS_OF_THE_WEEK.map((day) => {
              const hours = data.openingHours?.[day.en];
              const isToday = day.en === today;
              return (
                <div
                  key={day.en}
                  className="d-flex align-items-start justify-content-between py-2 px-2"
                  style={{
                    borderRadius: 8,
                    backgroundColor: isToday
                      ? "rgba(240,129,63,0.08)"
                      : undefined,
                    borderBottom: isToday ? undefined : "1px solid #f3f3f3",
                  }}
                >
                  <span
                    className="text-capitalize"
                    style={{ fontSize: 14, fontWeight: isToday ? 700 : 500 }}
                  >
                    {day.fr}
                    {isToday && (
                      <span
                        className="ms-2"
                        style={{ fontSize: 11, color: ACCENT }}
                      >
                        Aujourd'hui
                      </span>
                    )}
                  </span>
                  <div className="text-end" style={{ fontSize: 14 }}>
                    {hours?.ranges?.length ? (
                      hours.ranges.map((r, i) => (
                        <div key={i}>
                          {r.from} – {r.to}
                        </div>
                      ))
                    ) : hours?.openAllDay ? (
                      <Chip tone="success">Ouvert 24h/24</Chip>
                    ) : (
                      <Chip tone="danger">Fermé</Chip>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-muted mb-0">
              Pas de données à afficher pour le moment
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}

function OffersTab({
  offers,
  onDelete,
  deletingId,
}: {
  offers?: SpecialOffers[];
  onDelete: (id: string) => void;
  deletingId?: string;
}) {
  if (!offers?.length) return <EmptyState text="Aucune offre disponible." />;
  return (
    <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell style={thStyle}>Titre</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Statut</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Type</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Remise</CTableHeaderCell>
            <CTableHeaderCell style={{ ...thStyle, textAlign: "right" }}>
              Actions
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {offers.map((offer) => (
            <CTableRow key={offer.id}>
              <CTableDataCell style={tdStyle} className="fw-semibold">
                {offer.title}
              </CTableDataCell>
              <CTableDataCell style={tdStyle}>
                {offer?.status?.label ? (
                  <Chip tone={statusTone(offer.status.label)}>
                    {offer.status.label}
                  </Chip>
                ) : (
                  <Dash />
                )}
              </CTableDataCell>
              <CTableDataCell style={tdStyle}>
                <Chip>{offer.type}</Chip>
              </CTableDataCell>
              <CTableDataCell style={tdStyle}>
                {offer?.reductionPercent > 0 ? (
                  <Chip tone="warning">-{offer.reductionPercent}%</Chip>
                ) : (
                  <Dash />
                )}
              </CTableDataCell>
              <CTableDataCell style={tdStyle}>
                <div className="d-flex justify-content-end gap-2">
                  <IconAction title="Modifier" to={getOfferUpdateLink(offer)}>
                    <EditIcon width="16" height="16" fill={colors.secondary} />
                  </IconAction>
                  <IconAction
                    title="Supprimer"
                    danger
                    onClick={() => onDelete(offer.id)}
                    disabled={deletingId === offer.id}
                  >
                    {deletingId === offer.id ? (
                      <CSpinner size="sm" />
                    ) : (
                      <DeleteIcon
                        width="18"
                        height="18"
                        fill={colors.primary}
                      />
                    )}
                  </IconAction>
                </div>
              </CTableDataCell>
            </CTableRow>
          ))}
        </CTableBody>
      </CTable>
    </div>
  );
}

function EventsTab({
  events,
  onDelete,
  deletingId,
}: {
  events?: any[];
  onDelete: (id: string) => void;
  deletingId?: string;
}) {
  if (!events?.length) return <EmptyState text="Aucun événement disponible." />;
  return (
    <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell style={thStyle}>Événement</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Date</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Statut</CTableHeaderCell>
            <CTableHeaderCell style={{ ...thStyle, textAlign: "right" }}>
              Actions
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {events.map((event) => (
            <CTableRow key={event.id}>
              <CTableDataCell style={tdStyle}>
                <div
                  className="d-flex align-items-center gap-2"
                  style={{ minWidth: 200 }}
                >
                  <img
                    src={
                      event.image ||
                      event.carouselPicture ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(event.name ?? "")}`
                    }
                    alt=""
                    width={40}
                    height={40}
                    className="flex-shrink-0"
                    style={{ borderRadius: 10, objectFit: "cover" }}
                  />
                  <span className="fw-semibold">
                    {event.name ?? event.title}
                  </span>
                </div>
              </CTableDataCell>
              <CTableDataCell style={tdStyle}>
                {event.date || event.startDate ? (
                  new Date(event.date ?? event.startDate).toLocaleDateString()
                ) : (
                  <Dash />
                )}
              </CTableDataCell>
              <CTableDataCell style={tdStyle}>
                {event?.status?.label ? (
                  <Chip tone={statusTone(event.status.label)}>
                    {event.status.label}
                  </Chip>
                ) : (
                  <Dash />
                )}
              </CTableDataCell>
              <CTableDataCell style={tdStyle}>
                <div className="d-flex justify-content-end gap-2">
                  <IconAction
                    title="Modifier"
                    to={`/offers/update/event-offer/${event.id}`}
                  >
                    <EditIcon width="16" height="16" fill={colors.secondary} />
                  </IconAction>
                  <IconAction
                    title="Supprimer"
                    danger
                    onClick={() => onDelete(event.id)}
                    disabled={deletingId === event.id}
                  >
                    {deletingId === event.id ? (
                      <CSpinner size="sm" />
                    ) : (
                      <DeleteIcon
                        width="18"
                        height="18"
                        fill={colors.primary}
                      />
                    )}
                  </IconAction>
                </div>
              </CTableDataCell>
            </CTableRow>
          ))}
        </CTableBody>
      </CTable>
    </div>
  );
}

function PacksTab({
  packs,
  onDelete,
  deletingId,
}: {
  packs?: Pack[];
  onDelete: (id: string) => void;
  deletingId?: string;
}) {
  if (!packs?.length) return <EmptyState text="Aucun pack disponible." />;
  return (
    <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell style={thStyle}>Titre</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Prix</CTableHeaderCell>
            <CTableHeaderCell style={{ ...thStyle, textAlign: "right" }}>
              Actions
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {packs.map((pack) => (
            <CTableRow key={pack.id}>
              <CTableDataCell style={tdStyle} className="fw-semibold">
                {pack.title}
              </CTableDataCell>
              <CTableDataCell style={tdStyle}>{pack.price} DH</CTableDataCell>
              <CTableDataCell style={tdStyle}>
                <div className="d-flex justify-content-end gap-2">
                  <IconAction title="Modifier" to={`/packs/update/${pack.id}`}>
                    <EditIcon width="16" height="16" fill={colors.secondary} />
                  </IconAction>
                  <IconAction
                    title="Supprimer"
                    danger
                    onClick={() => onDelete(pack.id)}
                    disabled={deletingId === pack.id}
                  >
                    {deletingId === pack.id ? (
                      <CSpinner size="sm" />
                    ) : (
                      <DeleteIcon
                        width="18"
                        height="18"
                        fill={colors.primary}
                      />
                    )}
                  </IconAction>
                </div>
              </CTableDataCell>
            </CTableRow>
          ))}
        </CTableBody>
      </CTable>
    </div>
  );
}

function ReviewsTab({ reviews }: { reviews: Review[] }) {
  if (!reviews.length) return <EmptyState text="Aucun avis pour le moment." />;
  const average =
    reviews.reduce((sum, r) => sum + Number(r.rating ?? 0), 0) / reviews.length;

  return (
    <div className="d-flex flex-column gap-3">
      <div style={cardStyle} className="d-flex align-items-center gap-3">
        <Stars value={average} />
        <span className="text-muted" style={{ fontSize: 13 }}>
          {reviews.length} avis
        </span>
      </div>

      {reviews.map((review) => (
        <div key={review.id} style={cardStyle}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center gap-3">
              {review?.user?.profilePicture ? (
                <img
                  src={review.user.profilePicture}
                  alt=""
                  width={40}
                  height={40}
                  className="rounded-circle flex-shrink-0"
                  style={{ objectFit: "cover" }}
                />
              ) : (
                <span
                  className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0 fw-bold"
                  style={{
                    width: 40,
                    height: 40,
                    color: ACCENT,
                    backgroundColor: "rgba(240,129,63,0.14)",
                  }}
                >
                  {(review?.user?.fullName?.[0] ?? "?").toUpperCase()}
                </span>
              )}
              <div>
                <div className="fw-semibold text-capitalize">
                  {review?.user?.fullName}
                </div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {new Date(review.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>
            <Stars value={Number(review.rating ?? 0)} />
          </div>
          {review.comment && (
            <p className="mb-0" style={{ fontSize: 14 }}>
              {review.comment}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

const DELETE_CONFIG = {
  offer: { endpoint: "specials", queryKey: "getPlaceOffers" },
  pack: { endpoint: "packs", queryKey: "getPlacePacks" },
  event: { endpoint: "events", queryKey: "getPlaceEvents" },
} as const;

/* ─────────────── Page ─────────────── */

type DeleteTarget = { type: keyof typeof DELETE_CONFIG; id: string } | null;

export default function Place() {
  const { placeId } = useParams();
  const { token } = useCookie("vToken");
  const queryClient = useQueryClient();
  const { toastSuccess, toastError } = useToast();
  const [activeTab, setActiveTab] = useState<TabKey>("about");
  const [toDelete, setToDelete] = useState<DeleteTarget>(null);

  const enabled = Boolean(placeId);

  // placeId is part of every key: switching places must not show stale data
  const { data } = useQuery<Place>(
    ["getPlace", placeId],
    () => apiService.MakeGetRequest(`places/${placeId}`),
    { enabled },
  );
  const { data: offers } = useQuery<SpecialOffers[]>(
    ["getPlaceOffers", placeId],
    () => apiService.MakeGetRequest(`places/${placeId}/specials`),
    { enabled },
  );
  const { data: events } = useQuery<any[]>(
    ["getPlaceEvents", placeId],
    () =>
      apiService.MakeGetRequest(`events?placeId=${placeId}&limit=100`, token),
    { enabled: enabled && Boolean(data?.isEventsEnabled) },
  );
  const { data: packs } = useQuery<Pack[]>(
    ["getPlacePacks", placeId],
    () => apiService.MakeGetRequest(`places/${placeId}/packs`),
    { enabled },
  );
  const { data: services } = useQuery<Service[]>(
    ["getPlaceServices", placeId],
    () => apiService.MakeGetRequest(`places/${placeId}/services`),
    { enabled },
  );
  const { data: reviewsData } = useQuery<{ items: Review[] }>(
    ["getPlaceReviews", placeId],
    () => apiService.MakeGetRequest(`places/${placeId}/ratings`),
    { enabled },
  );

  const reviews = reviewsData?.items ?? [];

  const deleteMutation = useMutation(
    ({ type, id }: NonNullable<DeleteTarget>) =>
      apiService.MakeDeleteRequest(
        `${DELETE_CONFIG[type].endpoint}/${id}`,
        token,
      ),
  );

  const confirmDelete = () => {
    if (!toDelete) return;
    deleteMutation.mutate(toDelete, {
      onSuccess() {
        toastSuccess(general.fr.message.operationSuccess ?? "Supprimé");
        queryClient.invalidateQueries([
          DELETE_CONFIG[toDelete.type].queryKey,
          placeId,
        ]);
        setToDelete(null);
      },
      onError() {
        toastError(general.fr.message.operationFailed);
        setToDelete(null);
      },
    });
  };

  const deletingId = deleteMutation.isLoading
    ? deleteMutation.variables?.id
    : undefined;

  const counts = {
    services: services?.length ?? 0,
    offers: offers?.length ?? 0,
    // @ts-ignore
    events: events?.items?.length ?? 0,
    packs: packs?.length ?? 0,
    reviews: reviews.length,
  };

  const eventsEnabled = Boolean(data?.isEventsEnabled);

  return (
    <main className="px-2">
      {/* Header */}
      <div style={cardStyle} className="mb-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div
            className="d-flex align-items-center gap-3"
            style={{ minWidth: 0 }}
          >
            <img
              src={
                data?.logoImage ||
                data?.image ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(data?.name ?? "")}`
              }
              alt=""
              width={72}
              height={72}
              className="flex-shrink-0"
              style={{ borderRadius: 16, objectFit: "cover" }}
            />
            <div style={{ minWidth: 0 }}>
              <h5 className="fs-4 fw-bold mb-1 text-capitalize">
                {data?.name}
              </h5>
              <div className="d-flex align-items-center flex-wrap gap-2">
                {data?.category?.label && <Chip>{data.category.label}</Chip>}
                {data &&
                  (data.isVisible ? (
                    <Chip tone="success">Visible</Chip>
                  ) : (
                    <Chip tone="danger">Masqué</Chip>
                  ))}
                {data?.rating ? <Stars value={Number(data.rating)} /> : null}
                {Number(data?.averagePrice) > 0 && (
                  <Chip tone="warning">
                    Prix moyen : {data?.averagePrice} DH
                  </Chip>
                )}
              </div>
              {data?.address && (
                <div
                  className="d-flex align-items-center gap-2 mt-2 text-muted"
                  style={{ fontSize: 13 }}
                >
                  <LocationIcon width="18" height="18" />
                  {data.address}
                </div>
              )}
            </div>
          </div>

          {data?.id && (
            <Link
              to={`/places/update/${data.id}`}
              className="btn btn-outline-danger rounded-pill px-3"
            >
              Modifier
            </Link>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="d-flex flex-wrap gap-3 mb-3">
        <StatTile
          label="Services"
          value={counts.services}
          onClick={() => setActiveTab("services")}
        />
        <StatTile
          label="Offres"
          value={counts.offers}
          onClick={() => setActiveTab("offers")}
        />
        <StatTile
          label="Packs"
          value={counts.packs}
          onClick={() => setActiveTab("packs")}
        />
        {eventsEnabled && (
          <StatTile
            label="Événements"
            value={counts.events}
            onClick={() => setActiveTab("events")}
          />
        )}
        <StatTile
          label="Avis"
          value={counts.reviews}
          onClick={() => setActiveTab("reviews")}
        />
      </div>

      <PillTabs
        active={activeTab}
        onChange={setActiveTab}
        counts={counts}
        hidden={eventsEnabled ? [] : ["events"]}
      />

      {activeTab === "events" && (
        <EventsTab
          // @ts-ignore
          events={events?.items}
          deletingId={deletingId}
          onDelete={(id) => setToDelete({ type: "event", id })}
        />
      )}

      {activeTab === "about" && <AboutTab data={data} />}
      {activeTab === "services" && (
        <Services menuURL={data?.menuURL} services={services!} />
      )}
      {activeTab === "offers" && (
        <OffersTab
          offers={offers}
          deletingId={deletingId}
          onDelete={(id) => setToDelete({ type: "offer", id })}
        />
      )}
      {activeTab === "packs" && (
        <PacksTab
          packs={packs}
          deletingId={deletingId}
          onDelete={(id) => setToDelete({ type: "pack", id })}
        />
      )}
      {activeTab === "reviews" && <ReviewsTab reviews={reviews} />}

      <PromptModal
        open={Boolean(toDelete)}
        onAccept={confirmDelete}
        onClose={() => setToDelete(null)}
        loading={deleteMutation.isLoading}
        message="Voulez-vous vraiment supprimer cet élément ?"
      />
    </main>
  );
}
